import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import {
  errorMessage,
  useCapacity,
  useCpuWorkspace,
  useCpuWorkspaceLogs,
  useExtendCpuWorkspace,
  useStopCpuWorkspace,
} from "../api/queries";
import type { Capacity, CpuWorkspace } from "../api/types";
import { TERMINAL_WORKSPACE_STATES } from "../api/types";
import { useSession } from "../auth/SessionProvider";
import { ClusterProblem, isClusterProblem } from "../components/ClusterProblem";
import { WaitingList } from "../components/capacity/WaitingSection";
import { PageHeader } from "../components/layout/PageHeader";
import { Dialog } from "../components/ui/Dialog";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import { ConnectionBlock } from "../components/workspace/ConnectionBlock";
import { EventTimeline } from "../components/workspace/EventTimeline";
import { MountsList } from "../components/workspace/MountsList";
import {
  cpuWorkspaceHeadline,
  NO_RESIZE_NOTE,
  WORKSPACE_NEVER_IDLE_NOTE,
} from "../lib/cpu";
import {
  formatDateTime,
  formatElapsed,
  formatRemaining,
  pluralize,
} from "../lib/format";
import { rowsFromWaiting, waitingNote } from "../lib/waiting";
import {
  isTerminalWorkspaceState,
  workspaceStateLabel,
  workspaceTone,
} from "../lib/workload";

/** Extension lengths offered in the dialog, the workspace ones. */
const EXTEND_PRESETS: readonly number[] = [1, 2, 4, 8];

/**
 * The live line under the headline while a CPU workspace has no machine yet.
 *
 * The same two numbers the CPU fleet screen shows for the same machine — and
 * where nobody has ever timed a CPU machine's boot, it says how long the
 * machine has been coming without inventing how long it takes.
 *
 * When the server published a `waiting` object it is the only source: the
 * machine it names is the one whose arrival serves this request's turn, which
 * may be the request ahead's. The rest is the fallback for a bundle newer than
 * the API it is talking to, which is why the capacity payload is still read.
 *
 * Args:
 *     workspace: The workspace being watched.
 *     capacity: The measured capacity, when it could be read.
 *
 * Returns:
 *     One sentence, or null when there is nothing true to add.
 */
function waitingProgress(
  workspace: CpuWorkspace,
  capacity: Capacity | undefined,
): string | null {
  if (workspace.state !== "waiting_for_node") {
    return null;
  }
  if (workspace.waiting) {
    return waitingNote(workspace.waiting, "core");
  }
  const eta = capacity?.cpu?.node_start_eta_minutes ?? null;
  if (workspace.machine_requested_at !== null) {
    const since = formatElapsed(workspace.machine_requested_at);
    return eta === null
      ? `Machine requested ${since} ago. How long a CPU machine takes to start has never been timed here, so the console does not say.`
      : `Machine requested ${since} ago — they are usually ready in about ${eta} min.`;
  }
  const free = capacity?.cpu?.largest_free_machine_cores ?? null;
  if (free !== null) {
    return `${pluralize(free, "core")} free on the emptiest machine that is running.`;
  }
  return null;
}

/**
 * One CPU workspace: what it is doing, how to get in, and how to keep it.
 *
 * The workspace screen researchers already know, with cores where the GPU
 * count was and **two controls missing on purpose**. There is no resize — to
 * change the size you stop it and start another one — and there is no idle
 * countdown, because nothing on a CPU machine separates a computing process
 * from a waiting one and the console will not invent that measurement. Both
 * absences are said in a sentence rather than shown as a disabled button.
 *
 * Returns:
 *     The CPU workspace screen.
 */
export function CpuWorkspacePage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspace = useCpuWorkspace(id);
  const { me } = useSession();
  const extend = useExtendCpuWorkspace(id);
  const stop = useStopCpuWorkspace(id);
  const [dialog, setDialog] = useState<"extend" | "stop" | null>(null);
  const [hours, setHours] = useState<number>(EXTEND_PRESETS[1] ?? 2);
  const [logsOpen, setLogsOpen] = useState(false);

  const data = workspace.data;
  const pending = data?.state === "waiting_for_node";
  // Only the shared capacity query, and only while it says something the
  // researcher needs: the same cache the CPU screen fills.
  const capacity = useCapacity(pending);
  // A workspace that never reached a machine has no log. Offering a panel that
  // can only say "there is nothing to read" is worse than not offering it.
  const hasLogs = data?.has_logs ?? false;
  const showLogs = hasLogs && (data?.state === "failed" || logsOpen);
  const logs = useCpuWorkspaceLogs(id, showLogs);
  const live = data !== undefined && !TERMINAL_WORKSPACE_STATES.includes(data.state);

  const close = (): void => {
    setDialog(null);
    extend.reset();
    stop.reset();
  };

  const header = (
    <PageHeader
      eyebrow="CPU workspace"
      title="Your CPU workspace"
      back={{ to: "/cpu", label: "CPU" }}
      subtitle={data ? data.image : undefined}
      actions={
        live ? (
          <div className="actions">
            <button
              type="button"
              className="btn"
              onClick={() => setDialog("extend")}
            >
              Keep it longer
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => setDialog("stop")}
            >
              Stop
            </button>
          </div>
        ) : null
      }
    />
  );

  if (workspace.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Loading this workspace…" />
      </>
    );
  }

  if (workspace.isError) {
    const error = workspace.error;
    if (isClusterProblem(error)) {
      return (
        <>
          {header}
          <ClusterProblem error={error} onRetry={() => void workspace.refetch()} />
        </>
      );
    }
    if (error instanceof ApiError && (error.isNotFound || error.isForbidden)) {
      return (
        <>
          {header}
          <p className="state-block">
            This workspace does not exist any more, or it belongs to somebody
            else.
          </p>
        </>
      );
    }
    return (
      <>
        {header}
        <ErrorBlock error={error} onRetry={() => void workspace.refetch()} />
      </>
    );
  }

  const detail = workspace.data;
  const tone = workspaceTone(detail.state);
  const progress = waitingProgress(detail, capacity.data);

  return (
    <>
      {header}

      <section className="state-line" data-tone={tone}>
        <span className="badge" data-tone={tone}>
          <span className="badge__dot" aria-hidden="true" />
          {workspaceStateLabel(detail.state)}
        </span>
        <p className="state-line__text">{cpuWorkspaceHeadline(detail)}</p>
        {progress ? <p className="state-line__note">{progress}</p> : null}
      </section>

      {/* Only when somebody is really in front of it: at the head, the
          sentence above has already said so. */}
      {detail.waiting && detail.waiting.ahead.length > 0 ? (
        <WaitingList
          rows={rowsFromWaiting(detail.waiting, {
            id: detail.id,
            user: detail.user,
            gpus: null,
            cores: detail.cores,
          })}
          unit="core"
          you={me.username}
        />
      ) : null}

      <div className="split">
        <div className="split__main">
          <h2 className="section__title">What has happened</h2>
          <EventTimeline events={detail.events} />

          {showLogs ? (
            <div className="logs">
              <h3 className="section__title">
                {detail.started_at ? "Log" : "Startup log"}
              </h3>
              {logs.isPending ? (
                <LoadingBlock label="Reading the log…" />
              ) : logs.isError ? (
                <ErrorBlock error={logs.error} onRetry={() => void logs.refetch()} />
              ) : (
                <pre className="log">{logs.data || "The log is empty."}</pre>
              )}
            </div>
          ) : hasLogs ? (
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setLogsOpen(true)}
            >
              {detail.started_at ? "Show the log" : "Show the startup log"}
            </button>
          ) : (
            <p className="aside__note">
              {/* The state is what says a workspace has ended — never
                  `started_at`, which a resize and a re-placement both leave
                  behind them. Reading the field instead told a workspace
                  queued for a bigger machine that its log had gone back "when
                  this workspace ended", directly under a trace line saying it
                  was being opened again. */}
              {isTerminalWorkspaceState(detail.state)
                ? detail.node
                  ? "The log went back with the machine when this workspace ended."
                  : "There is no log to read: this workspace never reached a machine."
                : detail.started_at
                  ? "The log will be here again as soon as the workspace is back on a machine."
                  : "There is no log yet: this workspace has not reached a machine."}
            </p>
          )}
        </div>

        <aside className="split__aside">
          <h2 className="section__title">Connect</h2>
          {detail.connection?.ready ? (
            <ConnectionBlock connection={detail.connection} />
          ) : (
            <p className="aside__note">
              The connection details appear here as soon as the workspace is
              ready and its port is open.
            </p>
          )}

          <h2 className="section__title aside__spaced">Cores</h2>
          {/* No utilization, and no meter: allocation comes from the cluster
              and is exact, activity has no source here. A percentage on this
              screen would be invented. */}
          <p className="aside__meta">
            {pluralize(detail.cores, "core")}
            {detail.memory_gb > 0 ? ` and ${detail.memory_gb} GiB of memory` : ""}
            {" reserved for you"}
            {detail.node ? (
              <>
                {" on "}
                <span className="mono">{detail.node}</span>
              </>
            ) : null}
            . Other people&apos;s work shares the machine; what protects yours
            is what you were given.
          </p>
          <p className="aside__note">
            How busy those cores are is not shown, because nothing on a CPU
            machine can measure it — a waiting process and a computing one look
            the same from outside.
          </p>

          <h2 className="section__title aside__spaced">Time</h2>
          <p className="aside__meta">
            {isTerminalWorkspaceState(detail.state) ? (
              "This workspace is not running any more. Your files in /home are still there."
            ) : detail.expires_at ? (
              <>
                Stops in {formatRemaining(detail.expires_at)}, at{" "}
                {formatDateTime(detail.expires_at)}.
              </>
            ) : (
              "The clock starts when the workspace becomes usable."
            )}
          </p>
          <p className="aside__note">{WORKSPACE_NEVER_IDLE_NOTE}</p>

          <h2 className="section__title aside__spaced">Your folders</h2>
          <MountsList mounts={detail.mounts} />

          <h2 className="section__title aside__spaced">Changing its size</h2>
          <p className="aside__note">{NO_RESIZE_NOTE}</p>
        </aside>
      </div>

      {dialog === "extend" ? (
        <Dialog
          title="Keep this workspace longer"
          onClose={close}
          footer={
            <>
              <button type="button" className="btn" onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={extend.isPending}
                onClick={() =>
                  extend.mutate({ hours }, { onSuccess: () => setDialog(null) })
                }
              >
                {extend.isPending ? "Adding…" : `Add ${pluralize(hours, "hour")}`}
              </button>
            </>
          }
        >
          <p className="dialog__text">
            It currently stops in {formatRemaining(detail.expires_at)}. How much
            longer do you need it? Nothing else will stop it before then, and
            nothing will keep it after.
          </p>
          <fieldset className="choice-row">
            <legend className="sr-only">How many more hours</legend>
            {EXTEND_PRESETS.map((preset) => (
              <label
                key={preset}
                className="choice"
                data-selected={hours === preset}
              >
                <input
                  type="radio"
                  name="extend-hours"
                  className="sr-only"
                  value={preset}
                  checked={hours === preset}
                  onChange={() => setHours(preset)}
                />
                +{preset} h
              </label>
            ))}
          </fieldset>
          {extend.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(extend.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}

      {dialog === "stop" ? (
        <Dialog
          title="Stop this workspace"
          onClose={close}
          footer={
            <>
              <button type="button" className="btn" onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={stop.isPending}
                onClick={() =>
                  stop.mutate(undefined, {
                    onSuccess: () => {
                      setDialog(null);
                      navigate("/cpu");
                    },
                  })
                }
              >
                {stop.isPending ? "Stopping…" : "Stop it"}
              </button>
            </>
          }
        >
          <p className="dialog__text">
            Your {pluralize(detail.cores, "core")} go back immediately, ready
            for whoever needs them. Everything in your home folder is kept and
            waits for your next workspace; anything left in the fast local
            folder is lost.
          </p>
          {stop.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(stop.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}
